import {isDeviceConfirmed} from "../../../axios-config/authSession";

/**
 * The bridge between the HTTP layer and the "unlock device" dialog. The private Axios instance calls
 * `requestDeviceUnlock` (via `setOnDeviceUnconfirmed`) when a request is answered with 403 `DEVICE_UNCONFIRMED`;
 * the dialog host listens here and shows the dialog while a request is open.
 *
 * Exactly one request can be open: every caller gets the same promise, so any number of parallel requests (and
 * the header notice) share one dialog. The promise ends with `true` once the device is confirmed and with
 * `false` when the user cancels or the session ends; it never rejects.
 */

let openRequest = null
const listeners = new Set()

const notifyListeners = () => listeners.forEach(listener => listener())

/** @returns {Promise<boolean>} true once the device is confirmed */
export const requestDeviceUnlock = () => {
    // Confirmed in the meantime (e.g. in another tab): the waiting request is simply repeated.
    if (isDeviceConfirmed()) {
        return Promise.resolve(true)
    }
    if (!openRequest) {
        let resolve
        const promise = new Promise(resolver => {
            resolve = resolver
        })
        openRequest = {promise, resolve}
        notifyListeners()
    }
    return openRequest.promise
}

/** Ends the open request, if there is one: `true` after the confirmation, `false` for cancel or logout. */
export const settleDeviceUnlock = (confirmed) => {
    if (!openRequest) {
        return
    }
    const {resolve} = openRequest
    openRequest = null
    notifyListeners()
    resolve(confirmed === true)
}

export const isDeviceUnlockRequested = () => openRequest !== null

/** For `useSyncExternalStore`. */
export const subscribeDeviceUnlock = (listener) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
}
